
import { supabase } from "@/lib/supabase";
import { 
  InventoryItem, 
  InventoryItemHistory, 
  PurchaseRequest,
  InventoryStatus,
  PurchaseRequestStatus,
  InventoryCategory,
  StorageLocation
} from "./inventoryTypes";
import { Employee } from "../assignments/types";
import { useToast } from "@/hooks/use-toast";

// Fetch inventory items with employee names
export async function fetchInventoryItems(): Promise<InventoryItem[]> {
  try {
    const { data, error } = await supabase
      .from('inventory_items')
      .select(`
        *,
        assignee:employees!inventory_items_assignee_id_fkey(id, name),
        authorizer:employees!inventory_items_authorized_by_fkey(id, name)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Process data to include assignee and authorizer names
    return (data as any[]).map(item => ({
      ...item,
      assignee_name: item.assignee?.name || null,
      authorizer_name: item.authorizer?.name || null
    })) as InventoryItem[];
  } catch (error) {
    console.error('Error fetching inventory items:', error);
    throw error;
  }
}

// Fetch a single inventory item by ID
export async function fetchInventoryItemById(id: string): Promise<InventoryItem | null> {
  try {
    const { data, error } = await supabase
      .from('inventory_items')
      .select(`
        *,
        assignee:employees!inventory_items_assignee_id_fkey(id, name),
        authorizer:employees!inventory_items_authorized_by_fkey(id, name)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }

    // Process data to include assignee and authorizer names
    return {
      ...data,
      assignee_name: data.assignee?.name || null,
      authorizer_name: data.authorizer?.name || null
    } as InventoryItem;
  } catch (error) {
    console.error(`Error fetching inventory item ${id}:`, error);
    throw error;
  }
}

// Fetch history for an inventory item
export async function fetchInventoryItemHistory(itemId: string): Promise<InventoryItemHistory[]> {
  try {
    const { data, error } = await supabase
      .from('inventory_item_history')
      .select(`
        *,
        actor:employees!inventory_item_history_actor_id_fkey(id, name)
      `)
      .eq('item_id', itemId)
      .order('event_at', { ascending: false });

    if (error) throw error;

    // Process data to include actor name
    return (data as any[]).map(event => ({
      ...event,
      actor_name: event.actor?.name || null
    }));
  } catch (error) {
    console.error(`Error fetching history for item ${itemId}:`, error);
    throw error;
  }
}

// Fetch purchase requests with requester and approver names
export async function fetchPurchaseRequests(): Promise<PurchaseRequest[]> {
  try {
    const { data, error } = await supabase
      .from('purchase_requests')
      .select(`
        *,
        requester:employees!purchase_requests_requested_by_fkey(id, name),
        approver:employees!purchase_requests_approved_by_fkey(id, name)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Process data to include requester and approver names
    return (data as any[]).map(request => ({
      ...request,
      requester_name: request.requester?.name || null,
      approver_name: request.approver?.name || null
    }));
  } catch (error) {
    console.error('Error fetching purchase requests:', error);
    throw error;
  }
}

// Fetch storage locations
export async function fetchStorageLocations(): Promise<StorageLocation[]> {
  try {
    const { data, error } = await supabase
      .from('storage_locations')
      .select('*')
      .order('name');

    if (error) throw error;
    return data as StorageLocation[];
  } catch (error) {
    console.error('Error fetching storage locations:', error);
    throw error;
  }
}

// Create a new storage location
export async function createStorageLocation(name: string): Promise<StorageLocation> {
  try {
    const { data, error } = await supabase
      .from('storage_locations')
      .insert({ name })
      .select()
      .single();

    if (error) throw error;
    return data as StorageLocation;
  } catch (error) {
    console.error('Error creating storage location:', error);
    throw error;
  }
}

// Update an existing storage location
export async function updateStorageLocation(id: string, name: string): Promise<StorageLocation> {
  try {
    const { data, error } = await supabase
      .from('storage_locations')
      .update({ name })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as StorageLocation;
  } catch (error) {
    console.error(`Error updating storage location ${id}:`, error);
    throw error;
  }
}

// Delete a storage location
export async function deleteStorageLocation(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('storage_locations')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error(`Error deleting storage location ${id}:`, error);
    throw error;
  }
}

// Fetch employees for selection dropdowns
export async function fetchEmployees(): Promise<Employee[]> {
  try {
    const { data, error } = await supabase
      .from('employees')
      .select('id, name, role')
      .order('name');

    if (error) throw error;
    return data as Employee[];
  } catch (error) {
    console.error('Error fetching employees:', error);
    throw error;
  }
}

// Fetch employees from the current user's company
export async function fetchCompanyEmployees(): Promise<Employee[]> {
  try {
    // First, get the current user's profile to find their company_id
    const { data: userData } = await supabase.auth.getUser();
    
    if (!userData.user) {
      throw new Error("No authenticated user found");
    }
    
    // Get the user's company from the users view instead of profiles
    const { data: userInfo, error: userError } = await supabase
      .from('users')
      .select('company_id')
      .eq('user_id', userData.user.id)
      .single();
    
    if (userError) throw userError;
    
    if (!userInfo || !userInfo.company_id) {
      console.warn("User doesn't have a company associated with their profile");
      return [];
    }
    
    // Get employees from the user's company
    const { data: employees, error } = await supabase
      .from('employees')
      .select('id, name, role, company_id')
      .eq('company_id', userInfo.company_id)
      .order('name');
    
    if (error) throw error;
    return employees as Employee[];
    
  } catch (error) {
    console.error('Error fetching company employees:', error);
    // Return empty array in case of error to avoid breaking the UI
    return [];
  }
}

// Update inventory item status
export async function updateItemStatus(
  itemId: string, 
  newStatus: InventoryStatus, 
  assignee_id?: number | null,
  location?: string | null
): Promise<InventoryItem> {
  try {
    const updateData: Partial<InventoryItem> = {
      status: newStatus,
      status_changed_at: new Date().toISOString()
    };
    
    // Handle assignee and location based on status
    if (newStatus === 'Assigned' && assignee_id) {
      updateData.assignee_id = assignee_id;
      updateData.location = null;
    } else if (newStatus === 'Available') {
      updateData.assignee_id = null;
      updateData.location = location || '';
    }

    // Set current user as actor for the history
    await supabase.auth.setSession({
      access_token: '',
      refresh_token: ''
    });

    const { data, error } = await supabase
      .from('inventory_items')
      .update(updateData)
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;
    
    return data as InventoryItem;
  } catch (error) {
    console.error('Error updating item status:', error);
    throw error;
  }
}

// Create a new inventory item - ensure name is required and serial is only required for hardware
export async function createInventoryItem(
  item: Partial<InventoryItem> & { category: InventoryCategory; name: string }
): Promise<InventoryItem> {
  try {
    // Ensure category is always provided
    if (!item.category) {
      throw new Error("Category is required for inventory items");
    }
    
    // Ensure name is always provided
    if (!item.name) {
      throw new Error("Name is required for inventory items");
    }
    
    // Prepare the item data for insertion, including required fields
    const itemData: any = {
      ...item,
      name: item.name,
      category: item.category
    };
    
    // Set a default serial if not provided for non-hardware items
    if (item.category === "Hardware") {
      if (!item.serial) {
        throw new Error("Serial is required for hardware items");
      }
    } else if (!item.serial) {
      // Generate a default serial for non-hardware items
      itemData.serial = `${item.category.substring(0, 3)}-${Date.now()}`;
    }
    
    const { data, error } = await supabase
      .from('inventory_items')
      .insert(itemData)
      .select()
      .single();

    if (error) throw error;
    return data as InventoryItem;
  } catch (error) {
    console.error('Error creating inventory item:', error);
    throw error;
  }
}

// Update purchase request status
export async function updatePurchaseRequestStatus(
  requestId: string,
  newStatus: PurchaseRequestStatus,
  approver_id: number,
  actual_cost?: number,
  inventory_item?: Partial<InventoryItem> & { category: InventoryCategory; serial: string }
): Promise<PurchaseRequest> {
  try {
    const updateData: Partial<PurchaseRequest> = {
      status: newStatus,
      approved_by: approver_id,
      approved_at: new Date().toISOString()
    };
    
    if (actual_cost) {
      updateData.actual_cost = actual_cost;
    }
    
    // First, update the purchase request
    const { data, error } = await supabase
      .from('purchase_requests')
      .update(updateData)
      .eq('id', requestId)
      .select()
      .single();

    if (error) throw error;
    
    // If approved and purchased, create inventory item
    if (newStatus === 'Purchased' && inventory_item) {
      // Make sure we have required fields
      if (!inventory_item.category) {
        // Get the request to find its category
        const { data: request } = await supabase
          .from('purchase_requests')
          .select('category')
          .eq('id', requestId)
          .single();
          
        if (request) {
          inventory_item.category = request.category;
        } else {
          throw new Error("Could not determine category for inventory item");
        }
      }
      
      // Make sure we have a serial number
      if (!inventory_item.serial) {
        throw new Error("Serial number is required for inventory items");
      }
      
      // Set a default name if not provided
      const itemToCreate = {
        ...inventory_item,
        authorized_by: approver_id,
        // Ensure name is provided for the inventory item
        name: inventory_item.name || `Item from PR-${requestId.substring(0, 8)}`
      };
      
      await createInventoryItem(itemToCreate as Partial<InventoryItem> & { category: InventoryCategory; name: string });
      
      // Link inventory item to purchase request
      if (data.inventory_item_id) {
        await supabase
          .from('purchase_requests')
          .update({ inventory_item_id: data.inventory_item_id })
          .eq('id', requestId);
      }
    }
    
    return data as PurchaseRequest;
  } catch (error) {
    console.error('Error updating purchase request:', error);
    throw error;
  }
}

// Create a new purchase request - ensure category and requested_by are always provided
export async function createPurchaseRequest(
  request: Partial<PurchaseRequest> & { category: PurchaseRequest['category']; requested_by: number }
): Promise<PurchaseRequest> {
  try {
    // Ensure category is always provided
    if (!request.category) {
      throw new Error("Category is required for purchase requests");
    }
    
    // Ensure requested_by is always provided
    if (!request.requested_by) {
      throw new Error("Requester ID is required for purchase requests");
    }
    
    const { data, error } = await supabase
      .from('purchase_requests')
      .insert(request)
      .select()
      .single();

    if (error) throw error;
    return data as PurchaseRequest;
  } catch (error) {
    console.error('Error creating purchase request:', error);
    throw error;
  }
}

// Get items assigned to a specific employee
export async function getEmployeeInventoryItems(employeeId: number): Promise<InventoryItem[]> {
  try {
    const { data, error } = await supabase
      .from('inventory_items')
      .select('*')
      .eq('assignee_id', employeeId)
      .eq('status', 'Assigned');

    if (error) throw error;
    return data as InventoryItem[];
  } catch (error) {
    console.error(`Error fetching inventory for employee ${employeeId}:`, error);
    throw error;
  }
}

// Helper hook for inventory operations with toast notifications
export function useInventoryOperations() {
  const { toast } = useToast();
  
  const handleInventoryItemCreate = async (
    item: Partial<InventoryItem> & { category: InventoryItem['category']; name: string }
  ) => {
    try {
      await createInventoryItem(item);
      toast({
        title: "Success",
        description: "Inventory item created successfully",
      });
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create inventory item",
        variant: "destructive",
      });
      return false;
    }
  };
  
  const handleItemStatusUpdate = async (
    itemId: string, 
    newStatus: InventoryStatus, 
    assignee_id?: number | null,
    location?: string | null
  ) => {
    try {
      await updateItemStatus(itemId, newStatus, assignee_id, location);
      toast({
        title: "Success",
        description: `Item ${getStatusActionText(newStatus)} successfully`,
      });
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${getStatusActionText(newStatus).toLowerCase()} item`,
        variant: "destructive",
      });
      return false;
    }
  };
  
  const handlePurchaseRequestCreate = async (
    request: Partial<PurchaseRequest> & { category: PurchaseRequest['category']; requested_by: number }
  ) => {
    try {
      await createPurchaseRequest(request);
      toast({
        title: "Success",
        description: "Purchase request created successfully",
      });
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create purchase request",
        variant: "destructive",
      });
      return false;
    }
  };
  
  return {
    handleInventoryItemCreate,
    handleItemStatusUpdate,
    handlePurchaseRequestCreate
  };
}

// Helper hook for storage location operations with toast notifications
export function useStorageLocationOperations() {
  const { toast } = useToast();
  
  const handleLocationCreate = async (name: string) => {
    try {
      await createStorageLocation(name);
      toast({
        title: "Success",
        description: "Storage location created successfully",
      });
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create storage location",
        variant: "destructive",
      });
      return false;
    }
  };
  
  const handleLocationUpdate = async (id: string, name: string) => {
    try {
      await updateStorageLocation(id, name);
      toast({
        title: "Success",
        description: "Storage location updated successfully",
      });
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update storage location",
        variant: "destructive",
      });
      return false;
    }
  };
  
  const handleLocationDelete = async (id: string) => {
    try {
      await deleteStorageLocation(id);
      toast({
        title: "Success",
        description: "Storage location deleted successfully",
      });
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete storage location",
        variant: "destructive",
      });
      return false;
    }
  };
  
  return {
    handleLocationCreate,
    handleLocationUpdate,
    handleLocationDelete
  };
}

// Helper function for toast messages
function getStatusActionText(status: InventoryStatus): string {
  switch (status) {
    case 'Available': return 'Returned';
    case 'Assigned': return 'Assigned';
    case 'Sold': return 'Marked as sold';
    case 'Retired': return 'Retired';
    default: return 'Status updated';
  }
}
